import { Link, useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { AdvancedSection } from "@/modules/shell/AdvancedSection";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { createProduct } from "@/shared/api/inventory";
import { queryKeys } from "@/shared/query/keys";
import {
  productNewSchema,
  type ProductNewInput,
} from "@/modules/inventory/product-form";

export function ProductNewPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<ProductNewInput>({
    resolver: zodResolver(productNewSchema),
    defaultValues: {
      name: "",
      sku: "",
      barcode: "",
      kind: "stocked",
      posPrice: "",
      trackLot: false,
      trackSerial: false,
    },
  });

  const kind = form.watch("kind");

  const create = useMutation({
    mutationFn: async (raw: ProductNewInput) => {
      const body = productNewSchema.parse(raw);
      return createProduct(companySlug!, body);
    },
    onSuccess: async (data) => {
      if (!companySlug) return;
      await queryClient.invalidateQueries({
        queryKey: queryKeys.products.all(companySlug),
      });
      navigate(`/c/${companySlug}/products/${data.product.id}`, {
        replace: true,
      });
    },
  });

  if (!companySlug) return null;

  const submitError =
    create.error instanceof Error
      ? create.error.message
      : create.isError
        ? "Create failed"
        : null;

  return (
      <PageFrame>
      <PageHeader
        title="New product"
        description="Name and kind are enough to start. Open Advanced for SKU, barcode, and tracking."
      />

      <form
        className="mt-8 space-y-4"
        onSubmit={form.handleSubmit((values) => create.mutate(values))}
      >
        <div>
          <Label htmlFor="product-name">Name</Label>
          <Input
            id="product-name"
            className="mt-1"
            autoFocus
            {...form.register("name")}
          />
          {form.formState.errors.name && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">
              {form.formState.errors.name.message}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="product-kind">Kind</Label>
          <Select
            id="product-kind"
            className="mt-1"
            {...form.register("kind")}
          >
            <option value="stocked">Stocked</option>
            <option value="service">Service (non-stock)</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="product-pos-price">POS price (optional)</Label>
          <Input
            id="product-pos-price"
            className="mt-1 font-mono"
            inputMode="decimal"
            placeholder="0.00"
            {...form.register("posPrice")}
          />
        </div>
        <AdvancedSection subtitle="codes & tracking">
          <div>
            <Label htmlFor="product-sku">SKU</Label>
            <Input
              id="product-sku"
              className="mt-1 font-mono"
              {...form.register("sku")}
            />
          </div>
          <div>
            <Label htmlFor="product-barcode">Barcode</Label>
            <Input
              id="product-barcode"
              className="mt-1 font-mono"
              {...form.register("barcode")}
            />
          </div>
          {kind === "stocked" ? (
            <div className="space-y-2 text-sm">
              <p className="m-0 text-xs text-[var(--color-muted)]">
                Leave tracking off unless you need lot or serial capture later.
              </p>
              <label className="flex min-h-11 items-center gap-2">
                <input type="checkbox" {...form.register("trackLot")} />
                Track lot numbers
              </label>
              <label className="flex min-h-11 items-center gap-2">
                <input type="checkbox" {...form.register("trackSerial")} />
                Track serial numbers
              </label>
            </div>
          ) : null}
        </AdvancedSection>
        {submitError && <Alert variant="destructive">{submitError}</Alert>}
        <div className="flex gap-3">
          <Button
            type="submit"
            pending={create.isPending}
            pendingLabel="Saving…"
          >
            Save
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to={`/c/${companySlug}/products`} className="no-underline">
              Cancel
            </Link>
          </Button>
        </div>
      </form>
    </PageFrame>
  );
}
